import React, { useState } from 'react';
import { fmt, bandColor, CRIT_COLOR, CRIT_ABBR, CRIT_ORDER } from '../../lib/format.js';

// Lollipop — ranked comparison. rows: {label, value, max=5, color, valueText, sub}
export function Lollipop({ rows, max = 5, testid }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} data-testid={testid}>
      {rows.map((r, i) => {
        const clean = r.value === null || r.value === undefined;
        const pct = clean ? 0 : Math.max(0, Math.min(100, (r.value / (r.max || max)) * 100));
        const color = r.color || 'var(--primary)';
        return (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 74px', gap: 12, alignItems: 'center' }}>
            <div style={{
              fontSize: 12.5, color: 'var(--ink-2)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {r.label}
              {r.sub && <span style={{ color: 'var(--ink-4)', marginLeft: 6, fontSize: 11 }}>{r.sub}</span>}
            </div>
            <div style={{ position: 'relative', height: 22, display: 'flex', alignItems: 'center' }}>
              {/* baseline */}
              <div style={{
                position: 'absolute', left: 0, right: 0, top: '50%',
                height: 2, background: 'var(--line-2)', borderRadius: 1,
                transform: 'translateY(-50%)',
              }} />
              {/* filled segment */}
              <div className="anim-grow" style={{
                position: 'absolute', left: 0, top: '50%',
                height: 2, width: `calc(${pct}% + 1px)`,
                background: color, borderRadius: 1, transform: 'translateY(-50%)',
              }} />
              {/* end dot */}
              {!clean && (
                <div className="anim-grow" style={{
                  position: 'absolute', left: `calc(${pct}% - 6px)`, top: '50%',
                  width: 12, height: 12, borderRadius: '50%',
                  background: color, border: '2px solid var(--surface)',
                  boxShadow: `0 0 0 1.5px ${color}`,
                  transform: 'translateY(-50%)',
                }} />
              )}
            </div>
            <div className="tabular" style={{ fontSize: 12.5, fontWeight: 800, color: clean ? 'var(--ink-4)' : color, textAlign: 'right' }}>
              {r.valueText || (clean ? '—' : fmt(r.value, 2))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// HBars — horizontal bar list. rows: {label, value, max=5, color, valueText, sub}
export function HBars({ rows, max = 5, testid }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} data-testid={testid}>
      {rows.map((r, i) => {
        const pct = r.value === null ? 0 : Math.max(0, Math.min(100, (r.value / (r.max || max)) * 100));
        const color = r.color || 'var(--primary)';
        return (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 70px', gap: 12, alignItems: 'center' }}>
            <div style={{ fontSize: 12.5, color: 'var(--ink-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {r.label}
              {r.sub && <span style={{ color: 'var(--ink-4)', marginLeft: 6, fontSize: 11 }}>{r.sub}</span>}
            </div>
            <div style={{ position: 'relative', height: 10, background: 'var(--line-2)', borderRadius: 5, overflow: 'hidden' }}>
              <div className="anim-grow" style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 5 }} />
            </div>
            <div className="tabular" style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink)', textAlign: 'right' }}>
              {r.valueText || (r.value === null ? '—' : fmt(r.value, 2))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// StackedBandBar — 4 stacked segments (percentages)
export function StackedBandBar({ bands }) {
  const order = ['Excellent', 'Good', 'Moderate', 'Poor'];
  const total = order.reduce((a, k) => a + (bands[k] || 0), 0) || 1;
  return (
    <div style={{
      display: 'flex', height: 22, borderRadius: 6, overflow: 'hidden', border: '1px solid var(--line)',
    }}>
      {order.map((b) => {
        const v = bands[b] || 0;
        const pct = (v / total) * 100;
        if (pct <= 0) return null;
        const c = bandColor(b);
        return (
          <div
            key={b}
            title={`${b}: ${v.toFixed(0)}%`}
            style={{
              width: `${pct}%`, background: c,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 10.5, fontWeight: 700,
            }}
          >
            {pct >= 14 ? `${v.toFixed(0)}%` : ''}
          </div>
        );
      })}
    </div>
  );
}

// RingGauge — score 0..5, ring in tool colour
export function RingGauge({ score, color, size = 130, max = 5 }) {
  const r = size / 2 - 10;
  const C = 2 * Math.PI * r;
  const pct = score ? Math.max(0, Math.min(1, score / max)) : 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--line-2)" strokeWidth="10" />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke={color} strokeWidth="10" strokeLinecap="round"
        strokeDasharray={`${C * pct} ${C}`}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dasharray .6s ease-out' }}
      />
      <text x="50%" y="47%" textAnchor="middle" dominantBaseline="middle" fontSize="22" fontWeight="800" fill="var(--ink)" className="tabular">
        {fmt(score, 2)}
      </text>
      <text x="50%" y="63%" textAnchor="middle" fontSize="10" fill="var(--ink-3)" fontWeight="600">of 5.0</text>
    </svg>
  );
}

// WeightDonut — 7 segments proportional to weights
export function WeightDonut({ weights, size = 220 }) {
  const R = size / 2 - 8;
  const r = R - 26;
  const entries = CRIT_ORDER.map((c) => [c, weights[c]]);
  const total = entries.reduce((a, [, v]) => a + v, 0);
  let acc = 0;
  const cx = size / 2, cy = size / 2;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-label="Weight distribution">
      {entries.map(([c, w]) => {
        const start = (acc / total) * 2 * Math.PI;
        acc += w;
        const end = (acc / total) * 2 * Math.PI;
        const large = end - start > Math.PI ? 1 : 0;
        const p = (a, rr) => [cx + rr * Math.sin(a), cy - rr * Math.cos(a)];
        const [x1, y1] = p(start, R);
        const [x2, y2] = p(end, R);
        const [x3, y3] = p(end, r);
        const [x4, y4] = p(start, r);
        const d = `M ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2} L ${x3} ${y3} A ${r} ${r} 0 ${large} 0 ${x4} ${y4} Z`;
        return <path key={c} d={d} fill={CRIT_COLOR[c]} stroke="#fff" strokeWidth="1.5" />;
      })}
      <text x={cx} y={cy - 4} textAnchor="middle" fontSize="12" fontWeight="800" fill="var(--ink)">7 CRITERIA</text>
      <text x={cx} y={cy + 12} textAnchor="middle" fontSize="10" fill="var(--ink-3)" fontWeight="600">OECD–DAC</text>
    </svg>
  );
}

// Heatmap — rows (tools) × cols (criteria + Overall).
// values: value>=0..5 or null; band colour bg, white text.
export function Heatmap({ rows, cols, labels, valueAt, overallByRow, overallLabel = 'Overall' }) {
  return (
    <div className="scroll-x">
      <table style={{ borderCollapse: 'separate', borderSpacing: 4, width: '100%', minWidth: 640 }}>
        <thead>
          <tr>
            <th style={{ background: 'transparent', padding: 4 }} />
            {cols.map((c) => (
              <th key={c} style={{
                fontSize: 10.5, fontWeight: 700, color: 'var(--ink-3)',
                textTransform: 'uppercase', letterSpacing: '.08em', textAlign: 'center', padding: 4,
              }}>
                {labels?.[c] || c}
              </th>
            ))}
            <th style={{
              fontSize: 10.5, fontWeight: 700, color: 'var(--ink-3)',
              textTransform: 'uppercase', letterSpacing: '.08em', textAlign: 'center', padding: 4,
            }}>
              {overallLabel}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((rowKey, i) => (
            <tr key={rowKey}>
              <td style={{ fontWeight: 700, fontSize: 12, color: 'var(--ink-2)', padding: '6px 8px 6px 0' }}>
                {rowKey}
              </td>
              {cols.map((c) => {
                const v = valueAt(rowKey, c);
                if (v === null || v === undefined) {
                  return <td key={c} style={{ background: 'var(--line-2)', color: 'var(--ink-4)', textAlign: 'center', padding: '10px 6px', borderRadius: 6, fontSize: 12 }}>—</td>;
                }
                const b = v >= 4.6 ? 'Excellent' : v >= 3.6 ? 'Good' : v >= 2.6 ? 'Moderate' : 'Poor';
                const bg = bandColor(b);
                return (
                  <td key={c} style={{
                    background: bg, color: '#fff', textAlign: 'center', padding: '10px 6px',
                    borderRadius: 6, fontWeight: 700, fontSize: 12.5,
                  }} className="tabular" title={`${rowKey} × ${c}: ${fmt(v, 2)} (${b})`}>
                    {fmt(v, 2)}
                  </td>
                );
              })}
              {(() => {
                const v = overallByRow?.[rowKey];
                if (v === undefined || v === null) return <td>—</td>;
                const b = v >= 4.6 ? 'Excellent' : v >= 3.6 ? 'Good' : v >= 2.6 ? 'Moderate' : 'Poor';
                return (
                  <td style={{
                    background: bandColor(b), color: '#fff', textAlign: 'center',
                    padding: '10px 6px', borderRadius: 6, fontWeight: 800, fontSize: 13,
                    outline: '2px solid var(--ink)', outlineOffset: -2,
                  }} className="tabular">
                    {fmt(v, 2)}
                  </td>
                );
              })()}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ClusteredBars — 4 tool bars per unit. units: {unit, tools:{NRM,H&H,SDLE,PoE}}
export function ClusteredBars({ units, testid }) {
  const TOOLS = ['NRM', 'H&H', 'SDLE', 'PoE'];
  const COLORS = { NRM: '#04294f', 'H&H': '#2e7cbf', SDLE: '#e23d28', PoE: '#9b2d20' };
  const H = 200;
  return (
    <div data-testid={testid}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
        {units.map((u) => (
          <div key={u.unit} style={{ minWidth: 180, flex: '1 1 200px' }}>
            <div style={{ height: H, display: 'flex', alignItems: 'flex-end', gap: 6, borderBottom: '1px solid var(--line)', position: 'relative' }}>
              {/* y=5 gridline */}
              <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                {[1, 2, 3, 4, 5].map((y) => (
                  <div key={y} style={{
                    position: 'absolute', bottom: (y / 5) * H, left: 0, right: 0,
                    borderTop: '1px dashed var(--line-2)',
                  }} />
                ))}
              </div>
              {TOOLS.map((t) => {
                const v = u.tools?.[t];
                const h = v ? (v / 5) * H : 0;
                return (
                  <div key={t} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                    <div className="anim-grow" style={{
                      width: '100%', height: h, background: COLORS[t], borderRadius: '4px 4px 0 0',
                    }} title={`${t}: ${fmt(v, 2)}`} />
                    <div style={{ position: 'absolute', top: -18, fontSize: 10, fontWeight: 700, color: 'var(--ink-2)' }} className="tabular">
                      {v ? fmt(v, 2) : ''}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop: 6, display: 'flex', gap: 6, justifyContent: 'space-between' }}>
              {TOOLS.map((t) => (
                <div key={t} style={{ flex: 1, fontSize: 10, fontWeight: 600, color: COLORS[t], textAlign: 'center' }}>{t}</div>
              ))}
            </div>
            <div style={{ marginTop: 6, fontSize: 12.5, fontWeight: 700, color: 'var(--ink)' }}>{u.unit}</div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)' }} className="tabular">
              n={u.projects} · avg {fmt(u.avgScore, 2)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ScatterTrend — x=budgetCr, y=overall, band-coloured dots, dashed trend
export function ScatterTrend({ points, xLabel = 'Budget (₹ Cr)', yLabel = 'Overall score', testid }) {
  const W = 720, H = 340, P = { l: 44, r: 16, t: 16, b: 34 };
  const xs = points.map((p) => p.budgetCr);
  const ys = points.map((p) => p.overall);
  const xMax = Math.max(...xs, 1) * 1.05;
  const yMax = 5;
  const sx = (v) => P.l + (v / xMax) * (W - P.l - P.r);
  const sy = (v) => H - P.b - (v / yMax) * (H - P.t - P.b);

  // Linear regression
  const n = points.length;
  const meanX = xs.reduce((a, b) => a + b, 0) / n;
  const meanY = ys.reduce((a, b) => a + b, 0) / n;
  const num = xs.reduce((a, x, i) => a + (x - meanX) * (ys[i] - meanY), 0);
  const den = xs.reduce((a, x) => a + (x - meanX) * (x - meanX), 0);
  const slope = den ? num / den : 0;
  const intercept = meanY - slope * meanX;
  const trend = [0, xMax].map((x) => [x, slope * x + intercept]);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Budget vs score scatter" data-testid={testid}>
      {/* gridlines */}
      {[1, 2, 3, 4, 5].map((y) => (
        <g key={y}>
          <line x1={P.l} x2={W - P.r} y1={sy(y)} y2={sy(y)} stroke="var(--line-2)" strokeDasharray="3 3" />
          <text x={P.l - 6} y={sy(y) + 3} textAnchor="end" fontSize="10" fill="var(--ink-4)" className="tabular">{y}</text>
        </g>
      ))}
      {/* x ticks */}
      {[0, 0.25, 0.5, 0.75, 1].map((t) => (
        <g key={t}>
          <text x={sx(t * xMax)} y={H - P.b + 16} textAnchor="middle" fontSize="10" fill="var(--ink-4)" className="tabular">
            {(t * xMax).toFixed(0)}
          </text>
        </g>
      ))}
      {/* axis labels */}
      <text x={W / 2} y={H - 4} textAnchor="middle" fontSize="10.5" fontWeight="600" fill="var(--ink-3)">{xLabel}</text>
      <text x={12} y={H / 2} transform={`rotate(-90 12 ${H / 2})`} textAnchor="middle" fontSize="10.5" fontWeight="600" fill="var(--ink-3)">{yLabel}</text>
      {/* trend */}
      <line x1={sx(trend[0][0])} y1={sy(trend[0][1])} x2={sx(trend[1][0])} y2={sy(trend[1][1])}
        stroke="var(--ink-3)" strokeWidth="1.5" strokeDasharray="6 4" />
      {/* points */}
      {points.map((p, i) => (
        <circle key={i} cx={sx(p.budgetCr)} cy={sy(p.overall)} r={5.5}
          fill={bandColor(p.band)} fillOpacity="0.75" stroke="#fff" strokeWidth="1.2">
          <title>{`${p.code} · ${p.partner || ''} · ₹${p.budgetCr.toFixed(2)} Cr · ${p.overall.toFixed(2)}`}</title>
        </circle>
      ))}
    </svg>
  );
}

// TrendBars — vertical bars, HDFC blue gradient
export function TrendBars({ data, suffix = '' }) {
  const W = 720, H = 260, P = { l: 44, r: 12, t: 20, b: 30 };
  const max = Math.max(...data.map(([, v]) => v)) * 1.1;
  const bw = (W - P.l - P.r) / data.length - 10;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%">
      <defs>
        <linearGradient id="gradBars" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0a63b0" />
          <stop offset="100%" stopColor="#004c8f" />
        </linearGradient>
      </defs>
      {[0, 0.25, 0.5, 0.75, 1].map((t) => (
        <line key={t} x1={P.l} x2={W - P.r} y1={P.t + t * (H - P.t - P.b)} y2={P.t + t * (H - P.t - P.b)}
          stroke="var(--line-2)" strokeDasharray="3 3" />
      ))}
      {data.map(([label, v], i) => {
        const x = P.l + i * ((W - P.l - P.r) / data.length) + 5;
        const h = (v / max) * (H - P.t - P.b);
        const y = H - P.b - h;
        return (
          <g key={label}>
            <rect x={x} y={y} width={bw} height={h} rx="4" fill="url(#gradBars)" className="anim-grow" />
            <text x={x + bw / 2} y={y - 4} textAnchor="middle" fontSize="10" fontWeight="700" fill="var(--ink)" className="tabular">
              {fmt(v, v >= 100 ? 0 : 1)}{suffix}
            </text>
            <text x={x + bw / 2} y={H - P.b + 15} textAnchor="middle" fontSize="10" fill="var(--ink-3)">{label}</text>
          </g>
        );
      })}
    </svg>
  );
}

// TrendArea — area + line
export function TrendArea({ data, suffix = ' cr' }) {
  const W = 720, H = 260, P = { l: 44, r: 12, t: 20, b: 30 };
  const max = Math.max(...data.map(([, v]) => v)) * 1.1;
  const min = Math.min(...data.map(([, v]) => v)) * 0.9;
  const sx = (i) => P.l + (i / (data.length - 1)) * (W - P.l - P.r);
  const sy = (v) => H - P.b - ((v - min) / (max - min)) * (H - P.t - P.b);
  const pts = data.map(([, v], i) => [sx(i), sy(v)]);
  const path = pts.map((p, i) => `${i ? 'L' : 'M'} ${p[0]} ${p[1]}`).join(' ');
  const area = `${path} L ${pts[pts.length - 1][0]} ${H - P.b} L ${pts[0][0]} ${H - P.b} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%">
      <defs>
        <linearGradient id="gradArea" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#004c8f" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#004c8f" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[0, 0.5, 1].map((t) => (
        <line key={t} x1={P.l} x2={W - P.r} y1={P.t + t * (H - P.t - P.b)} y2={P.t + t * (H - P.t - P.b)}
          stroke="var(--line-2)" strokeDasharray="3 3" />
      ))}
      <path d={area} fill="url(#gradArea)" />
      <path d={path} fill="none" stroke="#004c8f" strokeWidth="2.5" />
      {data.map(([label, v], i) => (
        <g key={label}>
          <circle cx={sx(i)} cy={sy(v)} r="4" fill="#fff" stroke="#004c8f" strokeWidth="2" />
          <text x={sx(i)} y={sy(v) - 8} textAnchor="middle" fontSize="10" fontWeight="700" fill="var(--ink)" className="tabular">
            {fmt(v, 2)}
          </text>
          <text x={sx(i)} y={H - P.b + 15} textAnchor="middle" fontSize="10" fill="var(--ink-3)">{label}</text>
        </g>
      ))}
    </svg>
  );
}
